import { GlassWater, Store, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Group = { Icon: LucideIcon; title: string; lead: string; items: ReadonlyArray<string> };

const GROUPS: ReadonlyArray<Group> = [
  {
    Icon: GlassWater,
    title: 'Pour tes boissons',
    lead: 'Tout ce qui se sert au verre et se compte au millilitre.',
    items: [
      'Cocktails & mélanges',
      'Spiritueux (rhum, whisky, gin…)',
      'Bières pression & bouteille',
      'Vins au verre',
      'Sirops, jus & sodas',
      'Boissons maison & infusions',
    ],
  },
  {
    Icon: Store,
    title: 'Pour ton établissement',
    lead: 'Du comptoir de quartier à la cave d’hôtel.',
    items: [
      'Bar & bar à cocktails',
      'Lounge & club',
      'Maquis & buvette',
      'Restaurant avec bar',
      'Snack-bar',
      'Bar d’hôtel',
    ],
  },
  {
    Icon: Users,
    title: 'Pour ton équipe',
    lead: 'Chacun voit ce dont il a besoin, ni plus ni moins.',
    items: [
      'Gérant / responsable de salle',
      'Propriétaire & investisseur',
      'Chef de bar / barman',
      'Responsable des stocks',
      'Acheteur / approvisionnement',
      'Comptable de l’établissement',
    ],
  },
];

export default function BarForWhoV2() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>Pour qui</SectionLabel>
        <h2 className="mt-3 max-w-3xl font-marketing-display text-[clamp(30px,4.4vw,56px)] font-bold leading-[1.02] tracking-[-0.035em] text-charcoal-800">
          Fait pour les bars et restaurants{' '}
          <span className="underline-lime">d’Afrique.</span>
        </h2>
        <p className="mt-4 max-w-xl font-marketing-body text-[17px] leading-[1.55] text-charcoal-600">
          Si tu sers des boissons et que tu veux connaître ta marge réelle, le contrôle des coûts
          Velko Bar est pour toi — quel que soit ton format.
        </p>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {GROUPS.map(({ Icon, title, lead, items }) => (
            <div
              key={title}
              className="flex flex-col gap-5 border-2 border-charcoal-800 bg-paper p-7 shadow-neobrutal-4"
            >
              <span className="flex h-12 w-12 items-center justify-center border-2 border-charcoal-800 bg-lime-500 text-charcoal-800">
                <Icon className="h-[22px] w-[22px]" strokeWidth={2} aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-marketing-display text-[20px] font-bold leading-[1.15] tracking-[-0.01em] text-charcoal-800">
                  {title}
                </h3>
                <p className="mt-1.5 font-marketing-body text-[14px] leading-[1.5] text-charcoal-600">
                  {lead}
                </p>
              </div>
              <ul className="space-y-2.5 border-t-2 border-dashed border-charcoal-200 pt-5">
                {items.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 font-marketing-body text-[14.5px] text-charcoal-700"
                  >
                    <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center border-[1.5px] border-charcoal-800 bg-white text-charcoal-800">
                      <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.8">
                        <path d="M2.5 6l2.5 2.5 5-5" />
                      </svg>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
